import React, { useEffect, useState } from 'react';
import { withFormik, Field, Form, getIn } from 'formik';
import { Button, Row, Col } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import camelCaseRecursive from 'camelcase-keys-recursive';
import { FormikInput, FormikRangePicker, FormikSelect } from '../../components';
import { contractTypes, dateTypes } from './EmployeeForm/FormOptions';
import { searchEmployeeRequest } from '../../requests/employees';
import './style.scss';
import JobManagementsFilter from '../../components/Utils/Filters/JobManagementsFilter';
import JobTitlesFilter from '../../components/Utils/Filters/JobTitlesFilter';

const EmployeeFilter = props => {
  const { errors, touched, setFieldValue, setFieldTouched, values } = props;
  const dispatch = useDispatch();
  const [selector, setSelector] = useState([]);
  const { currentCompany } = useSelector(state => state.auth);
  const [searchDates, setSearchDates] = useState('');

  const searchEmployeesParams = () => {
    const request = async () =>
      searchEmployeeRequest({
        dispatch,
        params: {
          sort_column: 'name',
          paginate: false,
          advance: true
        },
        successCallback: response => setSelector(camelCaseRecursive(response.data.data))
      });
    request();
  };

  const fetchEmployeesFilter = searchValues => {
    const params = {
      filter_job_titles: [],
      filter_branch_offices: [],
      filter_job_managements: [],
      filter_syndicates: [],
      filter_cost_centers: [],
      filter_pension_fun: [],
      filter_health_previsions: [],
      filter_countries: [],
      filter_regions: [],
      filter_communes: []
    };
    searchValues.forEach(value => {
      params[value.queryAdvanceValue].push(value.value.split('-')[1]);
    });
    setFieldValue('filter_active_job_titles', params.filter_job_titles);
    setFieldValue('filter_active_branch_offices', params.filter_branch_offices);
    setFieldValue('filter_active_job_managements', params.filter_job_managements);
    setFieldValue('filter_active_syndicates', params.filter_syndicates);
    setFieldValue('filter_active_cost_centers', params.filter_cost_centers);
    setFieldValue('filter_pension_fun', params.filter_pension_fun);
    setFieldValue('filter_health_previsions', params.filter_health_previsions);
    setFieldValue('filter_countries', params.filter_countries);
    setFieldValue('filter_regions', params.filter_regions);
    setFieldValue('filter_communes', params.filter_communes);
  };

  const clearValues = () => {
    setFieldValue('dateStartContractFrom', '');
    setFieldValue('dateStartContractTo', '');
    setFieldValue('dateEndContractFrom', '');
    setFieldValue('dateEndContractTo', '');
    setFieldValue('dniExpirationDateFrom', '');
    setFieldValue('dniExpirationDateTo', '');
  };

  let startDateName;
  let endDateName;
  let startDate;
  let endDate;

  switch (searchDates) {
    case 'contract_start_date':
      startDateName = 'dateStartContractFrom';
      endDateName = 'dateStartContractTo';
      startDate = values.dateStartContractFrom;
      endDate = values.dateStartContractTo;
      break;

    case 'contract_end_date':
      startDateName = 'dateEndContractFrom';
      endDateName = 'dateEndContractTo';
      startDate = values.dateEndContractFrom;
      endDate = values.dateEndContractTo;
      break;

    default:
      startDateName = 'dniExpirationDateFrom';
      endDateName = 'dniExpirationDateTo';
      startDate = values.dniExpirationDateFrom;
      endDate = values.dniExpirationDateTo;
  }

  const initialFetch = () => {
    searchEmployeesParams();
  };
  useEffect(initialFetch, []);

  return (
    <>
      <Form className="employee-search-form pt-2 pl-4 mb-2 ">
        <Row>
          <Col xs={12} md={2}>
            <Field name="filterNationalId">
              {({ field }) => (
                <FormikInput
                  {...field}
                  label="N° de Identificación:"
                  error={getIn(errors, field.name)}
                  touched={getIn(touched, field.name)}
                />
              )}
            </Field>
          </Col>
          <Col xs={12} md={{ span: 2, offset: 1 }}>
            <Field name="filterName">
              {({ field }) => (
                <FormikInput
                  {...field}
                  label="Nombre:"
                  error={getIn(errors, field.name)}
                  touched={getIn(touched, field.name)}
                />
              )}
            </Field>
          </Col>
          <Col xs={12} md={{ span: 2, offset: 1 }}>
            <JobManagementsFilter formikValue="filterJobManagement" params={{ company_id: currentCompany }} />
          </Col>
          <Col xs={12} md={{ span: 2, offset: 1 }}>
            <JobTitlesFilter formikValue="filterJobTitle" params={{ company_id: currentCompany }} />
          </Col>
        </Row>
        <Row>
          <Col xs={12} md={2}>
            <Field name="filterContractType">
              {({ field }) => (
                <FormikSelect
                  {...field}
                  label="Tipo de Contrato"
                  placeholder="Tipo de Contrato"
                  onChange={data => setFieldValue(field.name, data ? data.value : '')}
                  setFieldTouched={() => setFieldTouched(field.name)}
                  options={contractTypes}
                  error={getIn(errors, field.name)}
                  touched={getIn(touched, field.name)}
                  isClearable
                />
              )}
            </Field>
          </Col>
          <Col xs={12} md={{ span: 2, offset: 1 }}>
            <Field name="filterDates">
              {({ field }) => (
                <FormikSelect
                  {...field}
                  label="Fechas"
                  placeholder="Seleccione Fecha"
                  options={dateTypes}
                  onChange={data => {
                    setSearchDates(data?.value);
                    clearValues();
                    setFieldValue(field.name, data ? data.value : '');
                  }}
                  setFieldTouched={() => setFieldTouched(field.name)}
                  error={getIn(errors, field.name)}
                  touched={getIn(touched, field.name)}
                  isClearable
                />
              )}
            </Field>
          </Col>
          <Col xs={12} md={{ span: 5, offset: 1 }}>
            <Field name="rangeDate">
              {({ field }) => (
                <FormikRangePicker
                  name={field.name}
                  minCurrentDate={false}
                  startDateName={startDateName}
                  endDateName={endDateName}
                  startDate={startDate}
                  endDate={endDate}
                  onChange={(start, end) => {
                    if (searchDates === 'contract_start_date') {
                      setFieldValue('dateStartContractFrom', start);
                      setFieldValue('dateStartContractTo', end);
                    } else if (searchDates === 'contract_end_date') {
                      setFieldValue('dateEndContractFrom', start);
                      setFieldValue('dateEndContractTo', end);
                    } else {
                      setFieldValue('dniExpirationDateFrom', start);
                      setFieldValue('dniExpirationDateTo', end);
                    }
                    setFieldValue('filterDateFrom', start);
                    setFieldValue('filterDateTo', end);
                  }}
                  showClearDates
                  error={getIn(errors, field.name)}
                  touched={getIn(touched, field.name)}
                  appendToBody
                  repositionPickerOnUpdate
                />
              )}
            </Field>
          </Col>
        </Row>
        <Row>
          <Col md={5} className="mb-2">
            <Field name="filterAdvance">
              {({ field }) => (
                <FormikSelect
                  {...field}
                  label="Filtro Avanzado"
                  isMulti
                  options={selector}
                  onChange={data => {
                    fetchEmployeesFilter(data || []);
                    setFieldValue(field.name, data ? data.value : '');
                  }}
                  setFieldTouched={() => setFieldTouched(field.name)}
                  error={getIn(errors, field.name)}
                  touched={getIn(touched, field.name)}
                />
              )}
            </Field>
          </Col>
          <Col md={{ span: 2, offset: 9 }} className="d-flex align-items-end justify-content-end mb-5">
            <Button className="btn-block" type="submit">
              FILTRAR
            </Button>
          </Col>
        </Row>
      </Form>
    </>
  );
};

const handleSubmit = (values, { props }) => {
  const { formRequest } = props;
  formRequest(values);
};

export default withFormik({
  mapPropsToValues: () => ({
    filterNationalId: '',
    filterName: '',
    filterContractType: '',
    filterJobManagement: '',
    filterJobTitle: '',
    filterDateFrom: '',
    filterDateTo: '',
    filterDates: '',
    dateStartContractFrom: '',
    dateStartContractTo: '',
    dateEndContractFrom: '',
    dateEndContractTo: '',
    dniExpirationDateFrom: '',
    dniExpirationDateTo: ''
  }),
  handleSubmit,
  enableReinitialize: true
})(EmployeeFilter);
